// pages/OrderCreatePage.jsx
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import CardAdmin from '../UI/CardAdmin';
import Select from '../UI/Select';
import Button from '../UI/Button';

const OrderCreatePage = () => {
    const navigate = useNavigate();
    const { user, logout } = useUser();
    const { control, handleSubmit, watch, register } = useForm({
        defaultValues: {
            productId: '',
            variationRaw: '',
            quantity: 1,
            deadline: '',
            priority: 'normal',
            delayPhaseManager: false
        }
    });

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const selectedProductId = watch('productId');
    const selectedProduct = products.find(p => p._id === selectedProductId);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productsRes  = await fetchWithAuth(
					`${process.env.REACT_APP_API_URL}/orders/products`, 
					{}, 
					logout
				);
                const productsData = await productsRes.json();
                setProducts(productsData);
            } catch (err) {
                setError('Ошибка загрузки данных');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [logout]);

    const onSubmit = async (formData) => {
        try {
            const { productId, variationRaw, quantity, deadline, priority, delayPhaseManager } = formData;
            const variation = JSON.parse(variationRaw);

            const orderData = {
                productId,
                variation,
                quantity: Number(quantity),
                deadline: deadline ? new Date(deadline).toISOString() : null,
                priority,
                delayPhaseManager
            };

            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders`,
                {
                    method: 'POST',
                    body: JSON.stringify(orderData),
                },
                logout
            );

            const created = await res.json();
            navigate(`/orders/${created.order._id}`);
        } catch (err) {
            setError('Ошибка создания заказа');
        }
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="text-red-500">{error}</div>;

    return (
        <div className="container mx-auto p-4">
            <CardAdmin title="Создание заказа">
                {error && <div className="text-red-500 mb-4">{error}</div>}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-gray-400 max-w-screen-md mx-auto">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Продукт</label>
                        <Controller
                            control={control}
                            name="productId"
                            rules={{ required: 'Выберите продукт' }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    options={products.map(p => ({ label: p.name, value: p._id }))}
                                    placeholder="Выберите продукт"
                                    className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                                />
                            )}
                        />
                    </div>

                    {selectedProduct && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-white">Вариация</label>
                            <Controller
                                control={control}
                                name="variationRaw"
                                rules={{ required: 'Выберите вариацию' }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={selectedProduct.variations.flatMap(v =>
                                            v.value.map(val => ({
                                                value: JSON.stringify({ key: v.key, value: val }),
                                                label: `${v.key}: ${val}`
                                            }))
                                        )}
                                        placeholder="Выберите вариацию"
                                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                                    />
                                )}
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Количество</label>
                        <input
                            {...register('quantity', { 
                                required: 'Укажите количество',
                                min: { value: 1, message: 'Минимум 1' }
                            })}
                            type="number"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Срок выполнения</label>
                        <input
                            {...register('deadline')}
                            type="datetime-local"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Приоритет</label>
                        <Controller
                            control={control}
                            name="priority"
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    options={[
                                        { value: 'low', label: 'Низкий' },
                                        { value: 'normal', label: 'Обычный' },
                                        { value: 'high', label: 'Высокий' },
                                        { value: 'urgent', label: 'Срочный' }
                                    ]}
                                    className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                                />
                            )}
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            {...register('delayPhaseManager')}
                            className="rounded bg-[#1E1E1E] border-[#3E3E42]"
                        />
                        <label className="text-sm text-white">Отложить назначение руководителей фаз</label>
                    </div>

                    <div className="flex justify-end">
                        <Button type="submit" variant="primary">
                            Создать заказ
                        </Button>
                    </div>
                </form>
            </CardAdmin>
        </div>
    );
};

export default OrderCreatePage;
